import type { BaseSyntheticEvent, PropsWithChildren } from 'react';
import { createContext, useCallback, useRef } from 'react';
import { useContextOrThrow } from '@elfsight/lib-utils';

type SubmitFn = (
  e?: BaseSyntheticEvent,
  extra?: Record<string, unknown>
) => Promise<void>;

const CheckoutFormSubmitContext = createContext<{
  submit: SubmitFn;
  setSubmit: (submit?: SubmitFn) => void;
} | null>(null);

export function CheckoutFormSubmitProvider({ children }: PropsWithChildren) {
  const submitRef = useRef<SubmitFn | undefined>();

  const submit = useCallback(
    (...args: Parameters<SubmitFn>): ReturnType<SubmitFn> => {
      if (submitRef.current) {
        return submitRef.current?.(...args);
      }

      throw new Error('Submit method does not exist');
    },
    []
  );

  const setSubmit = useCallback((submit: SubmitFn | undefined) => {
    submitRef.current = submit;
  }, []);

  return (
    <CheckoutFormSubmitContext.Provider
      value={{
        submit,
        setSubmit
      }}
    >
      {children}
    </CheckoutFormSubmitContext.Provider>
  );
}

export function useCheckoutFormSubmit() {
  return useContextOrThrow(CheckoutFormSubmitContext, 'CheckoutFormSubmit');
}
